import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CustomButton } from '../Infra/CustomButton/CustomButton';

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  disableBackdropClick?: boolean;
  keepMounted?: boolean;

  // Footer props
  cancelText?: string;
  submitText?: string;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  hideSubmit?: boolean;
  hideCancel?: boolean;

  // Custom footer
  customFooter?: React.ReactNode;

  // Additional styling
  width?: string;
  maxWidthOverride?: string;

  // Additional styling
  sx?: SxProps<Theme>;

  // Testing
  dataTestId: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  disableBackdropClick = false,
  keepMounted = false,
  cancelText = 'Cancel',
  submitText = 'Submit',
  onSubmit,
  submitDisabled = false,
  submitLoading = false,
  hideSubmit = false,
  hideCancel = false,
  customFooter,
  width,
  maxWidthOverride,
  sx,
  dataTestId,
}) => {
  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <Dialog
      data-testid={dataTestId}
      open={open}
      onClose={disableBackdropClick ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      keepMounted={keepMounted}
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...(width && { width }),
          ...(maxWidthOverride && { maxWidth: maxWidthOverride }),
          ...sx,
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 2, mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: subtitle ? 1 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon}
            <Typography
              data-testid="modal-title"
              variant="h6"
              sx={{ fontWeight: 600 }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton
            data-testid="modal-close-btn"
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent data-testid="modal-content" sx={{ p: 3 }}>
        {children}
      </DialogContent>

      <DialogActions
        sx={{ p: 3, pt: 2, borderTop: 1, borderColor: 'divider', gap: 1 }}
      >
        {customFooter || (
          <>
            {!hideCancel && (
              <CustomButton
                data-testid="modal-cancel-btn"
                onClick={onClose}
                variant="text"
                color="secondary"
                sx={{}}
              >
                {cancelText}
              </CustomButton>
            )}
            {!hideSubmit && (
              <CustomButton
                data-testid="modal-submit-btn"
                onClick={handleSubmit}
                variant="contained"
                disabled={submitDisabled}
                loading={submitLoading}
              >
                {submitText}
              </CustomButton>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BaseModal;
